import React, { forwardRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Globe, Mail, Phone } from 'lucide-react';

export const Invoice = forwardRef(({
  villaName,
  ownerName,
  ownerEmail,
  ownerPhone,
  villaCity,
  invoiceNo,
  dateIssued,
  bookings,
  upiId,
  dueDate,
}, ref) => {
  const totalAmount = bookings.reduce((sum, booking) => sum + booking.amount, 0);
  const companyWebsite = "www.villakosh.in";
  const companyEmail = "contact@villakosh.in";
  const companyPhone = "+91 8668737989";

  return (
    <div ref={ref} className="max-w-4xl mx-auto bg-white p-8 shadow-lg">
      {/* Header */}
      <div className="mb-12">
        <h1 className="text-4xl font-bold tracking-wider mb-8">VILLAKOSH</h1>
        
        <div className="grid grid-cols-2 gap-8">
          <div>
            <h2 className="text-xl font-semibold mb-4">VILLA DETAILS</h2>
            <p className="mb-2">{villaName}</p>
            <p className="mb-2">Owner: {ownerName}</p>
            <p className="mb-2">
              <Mail className="h-4 w-4 inline mr-2" />
              {ownerEmail}
            </p>
            <p className="mb-2">
              <Phone className="h-4 w-4 inline mr-2" />
              {ownerPhone}
            </p>
            <p>{villaCity}</p>
          </div>
          
          <div className="text-right">
            <h2 className="text-xl font-semibold mb-4">BILLING INVOICE</h2>
            <div className="space-y-2">
              <p>Date Issued: {dateIssued}</p>
              <p>Invoice No: {invoiceNo}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Billing Details */}
      <div className="mb-12">
        <h3 className="text-lg font-semibold border-b-2 border-gray-800 pb-2 mb-4">
          BILLING DETAILS FOR {villaName.toUpperCase()}
        </h3>
        
        <table className="w-full">
          <thead>
            <tr className="text-left">
              <th className="py-2">BOOKING ID</th>
              <th className="py-2">GUEST</th>
              <th className="py-2">CHECK-IN</th>
              <th className="py-2">GUEST COUNT</th>
              <th className="py-2 text-right">AMOUNT (₹100/GUEST)</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((booking) => (
              <tr key={booking.bookingId} className="border-b">
                <td className="py-3">#{booking.bookingId}</td>
                <td className="py-3">{booking.guestName}</td>
                <td className="py-3">{booking.checkIn}</td>
                <td className="py-3">{booking.guestCount}</td>
                <td className="py-3 text-right">₹{booking.amount}</td>
              </tr>
            ))}
            <tr className="font-semibold">
              <td colSpan={4} className="py-4 text-right">Total Amount</td>
              <td className="py-4 text-right">₹{totalAmount}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Payment Details */}
      <div className="grid grid-cols-2 gap-8 mb-12">
        <div className="bg-gray-100 p-6 rounded-lg">
          <h3 className="font-semibold mb-4">BANK INFO</h3>
          <div className="space-y-2">
            <p>Payments Details</p>
            <p>UPI: {upiId}</p>
            <p className="text-sm text-gray-600">Scan this QR code for direct payments</p>
          </div>
          <div className="mt-4">
            <QRCodeSVG
              value={`upi://pay?pa=${upiId}&pn=Villakosh&am=${totalAmount}&tn=Invoice_${invoiceNo}`}
              size={120}
              level="H"
              includeMargin={true}
            />
          </div>
        </div>
        
        <div className="text-right">
          <h3 className="font-semibold mb-4">DUE BY</h3>
          <p className="text-2xl font-bold">{dueDate}</p>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-auto">
        <div className="flex justify-between items-center border-t-2 border-gray-800 pt-4">
          <div>
            <p className="font-semibold mb-4">THANK YOU</p>
          </div>
          <div>
            <p className="font-semibold mb-4">VILLAKOSH</p>
          </div>
        </div>
        
        <div className="flex justify-between text-sm text-gray-600 mt-4">
          <div className="flex items-center">
            <Phone className="h-4 w-4 mr-2" />
            <span>{companyPhone}</span>
          </div>
          <div className="flex items-center">
            <Globe className="h-4 w-4 mr-2" />
            <a href={`mailto:${companyWebsite}`} target="_blank" rel="noopener noreferrer">{companyWebsite}</a>
          </div>
          <div className="flex items-center">
            <Mail className="h-4 w-4 mr-2" />
            <a href={`mailto:${companyEmail}`} target="_blank" rel="noopener noreferrer">{companyEmail}</a>
          </div>
        </div>
      </div>
    </div>
  );
});

Invoice.displayName = 'Invoice';